import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionLabel } from '@app/components/core';
import { useProductViewer } from '../../products/productDetail/productViewer';
import { AuditChangeDirection, AuditChanges, AuditCheckChange, AuditStanding, AuditVerdict } from './sources/quality';

/** The groups in the order the section reads, best news first. */
const DIRECTIONS: AuditChangeDirection[] = ['improved', 'regressed', 'added', 'removed', 'reassessed'];

/** The tab's own standing pill, passed in so this file does not import its parent. */
export type StandingPill = React.ComponentType<{
  verdict: AuditVerdict | null;
  standing?: AuditStanding | null;
  children: React.ReactNode;
}>;

/** Which way a count moving is good news: more checks meeting target is, more needing improvement is not. */
const GOOD_WHEN: Partial<Record<AuditStanding, 1 | -1>> = {
  meetsTarget: 1,
  atStandard: 1,
  improvementPlanned: -1,
};

export type DeltaTone = 'good' | 'bad' | 'neutral';

export const deltaTone = (standing: AuditStanding, delta: number): DeltaTone => {
  const good = GOOD_WHEN[standing];
  if (!good || !delta) return 'neutral';
  return Math.sign(delta) === good ? 'good' : 'bad';
};

/** `Q2` from `2026-Q2`, so the two sides of a row read as quarters rather than dates. */
const shortQuarter = (period: string | null): string | null => /Q[1-4]$/.exec(period ?? '')?.[0] ?? period;

/** Both sides as bare quarters within one year; across years the year stays, or 2025-Q3 and 2026-Q3 both read `Q3`. */
const rowSides = (since: string, period: string | null): { from: string | null; to: string | null } =>
  !!period && since.slice(0, 4) === period.slice(0, 4)
    ? { from: shortQuarter(since), to: shortQuarter(period) }
    : { from: since, to: period };

/** A side of a transition as the tab names it elsewhere: its standing, else absent, else the raw state for staff. */
const useSideLabel = (): ((standing: AuditStanding | null, state: AuditVerdict | null) => string | null) => {
  const { t } = useTranslation();
  const { isClient } = useProductViewer();
  return (standing, state) => {
    if (standing) return t(`quality.standing.${standing}`);
    if (!state) return t('quality.changes.absent');
    return isClient ? null : state;
  };
};

/** `+1` or `-1` beside a tally figure, matching how the app signs its other deltas. */
export const StandingDelta = ({
  delta,
  since,
  className,
}: {
  delta: number;
  since: string;
  className: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const label =
    delta > 0
      ? t('quality.changes.moreThan', { count: delta, period: since })
      : t('quality.changes.fewerThan', { count: -delta, period: since });
  return (
    <span className="ml-2 align-middle">
      <span className={`text-xs font-semibold ${className}`} aria-hidden>
        {`${delta > 0 ? '+' : ''}${delta}`}
      </span>
      <span className="sr-only">{label}</span>
    </span>
  );
};

const ChangeRow = ({
  change,
  sides,
  Pill,
}: {
  change: AuditCheckChange;
  sides: { from: string | null; to: string | null };
  Pill: StandingPill;
}): JSX.Element => {
  const sideLabel = useSideLabel();
  const from = sideLabel(change.fromStanding, change.from);
  const to = sideLabel(change.toStanding, change.to);
  return (
    // Wraps rather than a fixed grid: in a narrow column the transition drops under the name instead of squeezing it.
    <li className="flex flex-col gap-1 border-b border-border py-2 text-sm last:border-b-0 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
      <span className="min-w-0 break-words font-medium text-foreground sm:min-w-[12rem] sm:flex-1">
        {change.name ?? change.slug}
      </span>
      {!!from && !!to && (
        <span className="inline-flex flex-wrap items-center gap-1.5 sm:ml-auto sm:justify-end">
          <span className="text-xs text-muted-foreground">{[sides.from, from].filter(Boolean).join(' ')}</span>
          <span className="text-xs text-muted-foreground" aria-hidden>
            →
          </span>
          {!!sides.to && <span className="text-xs text-muted-foreground">{sides.to}</span>}
          <Pill verdict={change.to} standing={change.toStanding}>
            {to}
          </Pill>
        </span>
      )}
    </li>
  );
};

const ChangeGroup = ({
  direction,
  checks,
  sides,
  Pill,
}: {
  direction: AuditChangeDirection;
  checks: AuditCheckChange[];
  sides: { from: string | null; to: string | null };
  Pill: StandingPill;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <p className="text-sm font-semibold text-foreground">{t(`quality.changes.direction.${direction}`)}</p>
      <ul className="flex flex-col">
        {checks.map(change => (
          <ChangeRow key={change.slug} change={change} sides={sides} Pill={Pill} />
        ))}
      </ul>
    </div>
  );
};

/** Better and worse sit side by side on a wide screen; the rarer groups follow at full width. */
const PAIRED: AuditChangeDirection[] = ['improved', 'regressed'];

/** The product's results that moved since the compared quarter, grouped by direction. */
export const ChangesSection = ({
  changes,
  period,
  Pill,
}: {
  changes: AuditChanges;
  period: string | null;
  Pill: StandingPill;
}): JSX.Element => {
  const { t } = useTranslation();
  const { isClient } = useProductViewer();
  const sides = rowSides(changes.since, period);
  // A check the registry no longer knows has only a slug and raw states, so a client is shown nothing for it, as elsewhere on the tab.
  const shown = isClient ? changes.checks.filter(change => !!change.name) : changes.checks;
  const groups = DIRECTIONS.map(direction => ({
    direction,
    checks: shown.filter(change => change.direction === direction),
  })).filter(group => group.checks.length > 0);
  const paired = groups.filter(group => PAIRED.includes(group.direction));
  const rest = groups.filter(group => !PAIRED.includes(group.direction));

  return (
    <>
      <SectionLabel>{t('quality.changes.section', { period: changes.since })}</SectionLabel>
      <Card className="flex flex-col gap-4">
        <p className="text-sm text-foreground">
          {changes.changed
            ? t('quality.changes.summary', {
                changed: changes.changed,
                total: changes.total,
                better: changes.better,
                worse: changes.worse,
                same: changes.same,
                period: changes.since,
              })
            : t('quality.changes.unchanged', { total: changes.total, period: changes.since })}
        </p>
        {changes.checklistChanged && (
          <p className="rounded-md bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground">
            {changes.previousChecklistVersion && changes.checklistVersion
              ? t('quality.changes.checklistNote', {
                  from: changes.previousChecklistVersion,
                  to: changes.checklistVersion,
                })
              : t('quality.changes.checklistNoteUnversioned')}
          </p>
        )}
        {!!paired.length && (
          <div className="grid gap-4 xl:grid-cols-2">
            {paired.map(group => (
              <ChangeGroup
                key={group.direction}
                direction={group.direction}
                checks={group.checks}
                sides={sides}
                Pill={Pill}
              />
            ))}
          </div>
        )}
        {rest.map(group => (
          <ChangeGroup
            key={group.direction}
            direction={group.direction}
            checks={group.checks}
            sides={sides}
            Pill={Pill}
          />
        ))}
      </Card>
    </>
  );
};
